/**
 * DESPI Custom Cart Drawer — Pattern C (Loop-style)
 * Vanilla Web Component, no framework, no Dawn dependency.
 *
 * Public API:
 *   document.querySelector('dpc-cart-drawer').addItems([{id, quantity}, ...])
 *   document.querySelector('dpc-cart-drawer').open()
 *   document.querySelector('dpc-cart-drawer').close()
 *
 * Internal flow:
 *   addItems → POST /cart/add.js → refresh() → open()
 *   qty change → POST /cart/change.js → refresh()
 *   refresh: GET {path}?sections=dpc-cart-drawer → replace [data-content] innerHTML
 */

(function () {
  'use strict';

  const SECTION_ID = 'dpc-cart-drawer';
  const FETCH_HEADERS = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json',
  };

  class DpcCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId || SECTION_ID;
      this.isLoading = false;
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._previousFocus = null;
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('submit', this._onSubmit);
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('submit', this._onSubmit);
      document.removeEventListener('keydown', this._onKeydown);
    }

    /* ─────────── PUBLIC API ─────────── */

    async addItems(items) {
      if (!Array.isArray(items) || items.length === 0) return;
      if (this.isLoading) return;
      this._setLoading(true);
      let addSucceeded = false;
      try {
        const res = await this._fetchWithRetry('/cart/add.js', {
          method: 'POST',
          headers: FETCH_HEADERS,
          credentials: 'same-origin',
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Add failed: ${res.status}`);
        }
        addSucceeded = true;
        // Refresh section + open drawer. If refresh fails (transient network),
        // we still open with the stale-but-acceptable content already in the
        // DOM so the user sees their drawer — better than a silent failure
        // while the item IS already in the cart server-side.
        try {
          await this.refresh();
        } catch (refreshErr) {
          console.warn('[dpc-cart] refresh failed after successful add — opening with stale content:', refreshErr);
          this._showError("Cart updated — showing the latest available view. Refresh the page if needed.");
        }
        this.open();
      } catch (err) {
        console.error('[dpc-cart] addItems failed:', err);
        if (!addSucceeded) {
          this._showError(err.message || 'Could not add to cart');
        }
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * Fetch with retry + exponential backoff for transient network/server errors.
     * Skips retry on 4xx (client errors are not going to fix themselves). 5xx and
     * network failures get retried up to maxRetries times with 250ms, 750ms, 1500ms
     * delays — enough to ride out a flaky cell signal without making the user wait
     * forever on a real outage.
     */
    async _fetchWithRetry(url, options, maxRetries = 2) {
      let lastError;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const res = await fetch(url, options);
          // Client errors → return immediately, don't retry
          if (res.status >= 400 && res.status < 500) return res;
          if (res.ok) return res;
          lastError = new Error(`HTTP ${res.status}`);
        } catch (err) {
          lastError = err;
        }
        if (attempt < maxRetries) {
          const delay = 250 * Math.pow(3, attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      throw lastError;
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this._warmCheckout();
      this._previousFocus = document.activeElement;
      this.classList.add('is-open');
      document.documentElement.classList.add('dpc-no-scroll');
      // Free-shipping bar always fills from 0% on every drawer open — so the
      // user catches the progression animation each time they look at it.
      this._animateFreeShipFromZero();
      // Defer focus to allow transition to start
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('.dpc-close-btn');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      });
    }

    /**
     * Opening the drawer is a strong checkout-intent signal: warm up DNS+TLS to
     * the domains the Shopify checkout redirect chain will hit (shop.app
     * universal redirect, payment field iframes). Saves 300-1000ms on mobile.
     * Runs once per page; preconnect without crossorigin = navigation sockets.
     */
    _warmCheckout() {
      if (this._checkoutWarmed) return;
      this._checkoutWarmed = true;
      ['https://shop.app', 'https://checkout.pci.shopifyinc.com', 'https://portal.shopifycloud.com', 'https://checkout.shopifycs.com'].forEach((href) => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        document.head.appendChild(link);
      });
    }

    /**
     * Instant feedback on "Continue to Checkout": the POST /cart → shop.app →
     * checkout chain takes 1-3s on mobile with zero visual response. Flag the
     * button as loading the moment the form submits (no preventDefault — the
     * navigation proceeds; the state is naturally discarded with the page).
     */
    _onSubmit(event) {
      const form = event.target;
      if (!(form instanceof Element) || !form.classList.contains('dpc-checkout-form')) return;
      const btn = form.querySelector('.dpc-checkout-btn');
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
      }
    }

    /**
     * Free-shipping fill animation driven by requestAnimationFrame — manual frame
     * loop, no dependency on CSS transitions or WAAPI. Sets `style.width` to a
     * computed percentage on every frame, so the user sees the fill rise no matter
     * how the browser handles transition state batching.
     *
     * Sequence: snap to 0% with transition disabled → wait 500ms (drawer slide
     * 350ms + 150ms breathing room) → animate to target percentage over 1400ms
     * with an ease-out-cubic curve.
     */
    _animateFreeShipFromZero() {
      const fill = this.querySelector('.dpc-free-ship__fill');
      const bar = this.querySelector('.dpc-free-ship');
      if (!fill || !bar) return;
      const targetPct = parseFloat(fill.dataset.targetWidth || fill.style.width || '0');
      if (!isFinite(targetPct) || targetPct <= 0) return;
      const isUnlocked = bar.classList.contains('is-unlocked');
      // Respect the user's OS-level reduced-motion preference — no fill animation,
      // bar lands at its target instantly. Critical for accessibility and for users
      // whose iOS WebView (Meta) forces reduced motion.
      const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        fill.style.transition = 'none';
        fill.style.width = targetPct + '%';
        if (isUnlocked) bar.classList.add('is-unlocked');
        return;
      }
      const duration = isUnlocked ? 1200 : 1400;
      // Cubic ease-out (Apple-ish) for the locked path; quintic for the unlock for
      // a snappier feel + the celebration glow.
      const ease = isUnlocked
        ? (t) => 1 - Math.pow(1 - t, 5)
        : (t) => 1 - Math.pow(1 - t, 3);

      // Snap to 0% with no transition — guaranteed instant, no flash at target.
      fill.style.transition = 'none';
      fill.style.width = '0%';
      // Force the 0% state to commit into layout
      // eslint-disable-next-line no-unused-expressions
      fill.offsetHeight;

      setTimeout(() => {
        const startTime = performance.now();
        const step = (now) => {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / duration, 1);
          const eased = ease(t);
          fill.style.width = (eased * targetPct).toFixed(2) + '%';
          if (t < 1) {
            requestAnimationFrame(step);
          } else {
            fill.style.width = targetPct + '%';
            // Fire the completion celebration the moment the fill actually
            // reaches its target — strongest when the bar lands on 100%
            // (free-shipping unlocked), but the regular pulse fires on every
            // open so the user always gets a confirmation of the arrival.
            if (isUnlocked) {
              bar.classList.add('is-just-unlocked');
              setTimeout(() => bar.classList.remove('is-just-unlocked'), 1100);
            } else {
              bar.classList.add('is-just-updated');
              setTimeout(() => bar.classList.remove('is-just-updated'), 750);
            }
          }
        };
        requestAnimationFrame(step);
      }, 500);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('dpc-no-scroll');
      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        this._previousFocus.focus({ preventScroll: true });
      }
      this._previousFocus = null;
    }

    /**
     * Update every header cart badge (`[data-cart-count]`) to reflect the
     * current server-side cart.item_count. Called after every successful
     * refresh() so the badge stays in sync with the drawer state.
     *
     * Animates a small bump on count INCREASE only (item added) — gives
     * delightful feedback without bumping on decrement (which would feel
     * weird after the user just removed an item).
     */
    _updateCartBadges(itemCount) {
      const count = Number.isFinite(itemCount) ? itemCount : null;
      const apply = (n) => {
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          const prev = parseInt(el.textContent || '0', 10);
          el.textContent = String(n);
          if (n === 0) {
            el.setAttribute('hidden', '');
          } else {
            el.removeAttribute('hidden');
          }
          if (n > prev) {
            el.classList.remove('is-bumped');
            // eslint-disable-next-line no-unused-expressions
            el.offsetHeight;
            el.classList.add('is-bumped');
            setTimeout(() => el.classList.remove('is-bumped'), 500);
          }
        });
        // Update aria-label on the cart button(s) so screen readers hear the new count
        document.querySelectorAll('[data-cart-open]').forEach((btn) => {
          btn.setAttribute('aria-label', `Open cart, ${n} item${n === 1 ? '' : 's'}`);
        });
      };
      if (count !== null) {
        apply(count);
        return;
      }
      // No count provided — fetch the authoritative server state.
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => apply(cart.item_count || 0))
        .catch((err) => console.warn('[dpc-cart] Could not refresh cart badge:', err));
    }

    /* ─────────── REFRESH (Sections Rendering API) ─────────── */

    async refresh() {
      // Capture which bundle signatures were expanded before refresh so we can restore after
      const expandedSigs = new Set();
      this.querySelectorAll('.dpc-item--bundle.is-expanded').forEach((el) => {
        const sig = el.dataset.bundleSignature;
        if (sig) expandedSigs.add(sig);
      });

      // Capture the previous free-shipping state so we can animate the bar across the innerHTML replacement
      const oldFill = this.querySelector('.dpc-free-ship__fill');
      const oldFillWidth = oldFill ? oldFill.style.width : null;
      const oldBar = this.querySelector('.dpc-free-ship');
      const wasUnlocked = oldBar ? oldBar.classList.contains('is-unlocked') : false;

      const path = window.location.pathname || '/';
      const url = `${path}?sections=${this.sectionId}`;
      const res = await this._fetchWithRetry(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
      const json = await res.json();
      const html = json[this.sectionId];
      if (!html) throw new Error('Section HTML missing from response');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-content]');
      const current = this.querySelector('[data-content]');
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
      }

      // Refresh the header cart badge from the authoritative server-side count.
      this._updateCartBadges();

      // Restore expanded bundle state (was lost when innerHTML was replaced)
      expandedSigs.forEach((sig) => {
        const card = this.querySelector(`.dpc-item--bundle[data-bundle-signature="${CSS.escape(sig)}"]`);
        if (!card) return;
        card.classList.add('is-expanded');
        const toggleBtn = card.querySelector('[data-action="toggle-bundle"]');
        if (toggleBtn) {
          toggleBtn.setAttribute('aria-expanded', 'true');
          toggleBtn.setAttribute('aria-label', 'Hide bundle details');
        }
        const expandedEl = card.querySelector('.dpc-bundle-expanded');
        if (expandedEl) expandedEl.removeAttribute('hidden');
      });

      // Free-shipping bar animation after innerHTML replacement:
      //   - Drawer was OPEN: animate incrementally from previous width → new target
      //     so the user sees the bar growing in response to their action.
      //   - Drawer was CLOSED: ATC-style flow (refresh → open). Park at 0% with no
      //     transition; open() will play the full 0 → target fill animation right
      //     after the slide-in completes.
      const newFill = this.querySelector('.dpc-free-ship__fill');
      const newBar = this.querySelector('.dpc-free-ship');
      if (newFill && newBar) {
        const targetPct = parseFloat(newFill.dataset.targetWidth || newFill.style.width || '0');
        if (this.classList.contains('is-open')) {
          const startPct = oldFillWidth ? parseFloat(oldFillWidth) : 0;
          const justUnlocked = !wasUnlocked && newBar.classList.contains('is-unlocked');
          // Park at old width with no transition (frame-batched with innerHTML)
          newFill.style.transition = 'none';
          newFill.style.width = startPct + '%';
          // eslint-disable-next-line no-unused-expressions
          newFill.offsetHeight;
          // Animate startPct → targetPct manually via requestAnimationFrame.
          setTimeout(() => {
            if (startPct !== targetPct && isFinite(targetPct)) {
              const duration = justUnlocked ? 1100 : 1200;
              const ease = justUnlocked
                ? (t) => 1 - Math.pow(1 - t, 5)
                : (t) => 1 - Math.pow(1 - t, 3);
              const startTime = performance.now();
              const step = (now) => {
                const elapsed = now - startTime;
                const t = Math.min(elapsed / duration, 1);
                const eased = ease(t);
                const current = startPct + (targetPct - startPct) * eased;
                newFill.style.width = current.toFixed(2) + '%';
                if (t < 1) {
                  requestAnimationFrame(step);
                } else {
                  newFill.style.width = targetPct + '%';
                  // Trigger celebration / pulse at the moment the bar reaches
                  // its target — unlock animation when crossing into 100% free
                  // shipping, regular pulse otherwise.
                  if (justUnlocked) {
                    newBar.classList.add('is-just-unlocked');
                    setTimeout(() => newBar.classList.remove('is-just-unlocked'), 1200);
                  } else {
                    newBar.classList.add('is-just-updated');
                    setTimeout(() => newBar.classList.remove('is-just-updated'), 750);
                  }
                }
              };
              requestAnimationFrame(step);
            }
          }, 60);
        } else {
          // Drawer closed — open() will animate from 0%. Park at 0% with no transition
          // so the fresh innerHTML doesn't flash at target width before open() fires.
          newFill.style.transition = 'none';
          newFill.style.width = '0%';
          // eslint-disable-next-line no-unused-expressions
          newFill.offsetHeight;
          newFill.style.transition = '';
        }
      }
    }

    /**
     * Shipping Protection toggle: ON adds the protection variant,
     * OFF sets its quantity to 0 via /cart/update.js.
     * Optimistic UI: the switch flips instantly on tap; the server
     * re-render (refresh) confirms, and the flip is rolled back on error.
     */
    async _toggleProtection(btn) {
      if (btn.dataset.busy === '1') return;
      btn.dataset.busy = '1';
      const variantId = btn.dataset.variantId;
      const wasOn = btn.classList.contains('is-on');
      btn.classList.toggle('is-on', !wasOn);
      btn.setAttribute('aria-checked', String(!wasOn));
      try {
        if (wasOn) {
          const res = await this._fetchWithRetry('/cart/update.js', {
            method: 'POST',
            headers: FETCH_HEADERS,
            credentials: 'same-origin',
            body: JSON.stringify({ updates: { [variantId]: 0 } }),
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
        } else {
          const res = await this._fetchWithRetry('/cart/add.js', {
            method: 'POST',
            headers: FETCH_HEADERS,
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
        }
        await this.refresh();
      } catch (err) {
        console.warn('[dpc-cart] shipping protection toggle failed:', err);
        btn.classList.toggle('is-on', wasOn);
        btn.setAttribute('aria-checked', String(wasOn));
      } finally {
        btn.dataset.busy = '';
      }
    }

    /* ─────────── EVENT HANDLERS ─────────── */

    _onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Close triggers (overlay, close button, anything with [data-close])
      if (target.closest('[data-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      // Bundle chevron toggle (must check before generic .dpc-qty__btn since this is a separate button)
      const toggleBtn = target.closest('[data-action="toggle-bundle"]');
      if (toggleBtn) {
        event.preventDefault();
        const card = toggleBtn.closest('.dpc-item--bundle');
        if (card) {
          const expanded = card.classList.toggle('is-expanded');
          toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          toggleBtn.setAttribute('aria-label', expanded ? 'Hide bundle details' : 'Show bundle details');
          const expandedEl = card.querySelector('.dpc-bundle-expanded');
          if (expandedEl) {
            if (expanded) expandedEl.removeAttribute('hidden');
            else expandedEl.setAttribute('hidden', '');
          }
        }
        return;
      }

      // Shipping Protection toggle
      const protectBtn = target.closest('[data-action="toggle-protection"]');
      if (protectBtn) {
        event.preventDefault();
        this._toggleProtection(protectBtn);
        return;
      }

      // Bundle qty controls (must check BEFORE generic .dpc-qty__btn — bundle buttons share that class)
      const bundleQtyBtn = target.closest('[data-action="bundle-increment"], [data-action="bundle-decrement"]');
      if (bundleQtyBtn) {
        event.preventDefault();
        const action = bundleQtyBtn.dataset.action;
        const sig = bundleQtyBtn.dataset.bundleSignature;
        if (action === 'bundle-increment') {
          this._handleBundleIncrement(sig);
        } else {
          const totalUnits = parseInt(bundleQtyBtn.dataset.totalUnits || '0', 10);
          this._handleBundleDecrement(sig, totalUnits);
        }
        return;
      }

      // Generic per-line qty buttons (single items, plus per-item controls inside expanded bundle)
      const qtyBtn = target.closest('.dpc-qty__btn[data-line]');
      if (qtyBtn) {
        event.preventDefault();
        this._handleQtyClick(qtyBtn);
        return;
      }
    }

    _onKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
      }
    }

    /* ─────────── QUANTITY CHANGE ─────────── */

    async _handleQtyClick(btn) {
      if (this.isLoading) return;
      const line = parseInt(btn.dataset.line, 10);
      const action = btn.dataset.action;
      // For inner-bundle-line buttons, closest('.dpc-item') jumps to the bundle
      // wrapper (outer card) — the inner LI is .dpc-bundle-line. To get the
      // correct qty value, look only within the button's own .dpc-qty group.
      const qtyGroup = btn.closest('.dpc-qty');
      const itemEl = btn.closest('.dpc-item, .dpc-bundle-line');
      if (!itemEl || !line || !action) return;

      const valueEl = qtyGroup ? qtyGroup.querySelector('.dpc-qty__value') : null;
      const currentQty = parseInt(valueEl ? valueEl.textContent.trim() : '0', 10);
      const newQty = action === 'increment' ? currentQty + 1 : Math.max(0, currentQty - 1);

      this._setLoading(true);
      itemEl.classList.add('is-updating');
      try {
        const res = await this._fetchWithRetry('/cart/change.js', {
          method: 'POST',
          headers: FETCH_HEADERS,
          credentials: 'same-origin',
          body: JSON.stringify({ line, quantity: newQty }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Update failed: ${res.status}`);
        }
        await this.refresh();
      } catch (err) {
        console.error('[dpc-cart] qty change failed:', err);
        this._showError(err.message || 'Could not update quantity');
        itemEl.classList.remove('is-updating');
      } finally {
        this._setLoading(false);
      }
    }

    /* ─────────── BUNDLE QTY ─────────── */

    /**
     * Bundle increment — add ONE PetTag to the bundle (not clone the bundle).
     * Picks the first cart line matching this signature and increments its qty by 1.
     * On next refresh the section recomputes sig_total_units → re-types DUO ↔ PACK
     * automatically and pulls the matching composite image.
     */
    async _handleBundleIncrement(signature) {
      if (this.isLoading || !signature) return;
      this._setLoading(true);
      try {
        const cartRes = await this._fetchWithRetry('/cart.js', { credentials: 'same-origin' });
        const cart = await cartRes.json();
        const target = cart.items.find((i) => i.properties && i.properties._bundle_signature === signature);
        if (!target) throw new Error('Bundle signature not found in cart');
        const res = await this._fetchWithRetry('/cart/change.js', {
          method: 'POST',
          headers: FETCH_HEADERS,
          credentials: 'same-origin',
          body: JSON.stringify({ id: target.key, quantity: target.quantity + 1 }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Bundle increment failed: ${res.status}`);
        }
        await this.refresh();
      } catch (err) {
        console.error('[dpc-cart] bundle increment failed:', err);
        this._showError(err.message || 'Could not add to bundle');
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * Bundle decrement — two modes based on the button's data-total-units:
     *   totalUnits > 1: remove ONE PetTag (decrement a line by 1, or remove a
     *     qty=1 line if no line has qty>1). The bundle card stays visible and
     *     re-types DUO ↔ PACK ↔ SOLO via sig_total_units on next refresh.
     *   totalUnits == 1: last PetTag in the bundle, trash-icon click — wipe
     *     ALL lines under this signature so the bundle card disappears.
     * Uses /cart/update.js with line-position-indexed updates: only the targeted
     * line indices change, all others are preserved by sending their current qty.
     * (Avoids any "remove by id" semantic that could ambiguate variant vs. line key.)
     */
    async _handleBundleDecrement(signature, totalUnits) {
      if (this.isLoading || !signature) return;
      this._setLoading(true);
      try {
        const cartRes = await this._fetchWithRetry('/cart.js', { credentials: 'same-origin' });
        const cart = await cartRes.json();
        const allItems = cart.items;
        const sigIndices = [];
        allItems.forEach((item, idx) => {
          if (item.properties && item.properties._bundle_signature === signature) {
            sigIndices.push(idx);
          }
        });
        if (sigIndices.length === 0) throw new Error('Bundle signature not found');

        // Build a positional updates array (length === cart.items.length).
        // Start by preserving every line's current qty.
        const updates = allItems.map((i) => i.quantity);

        if (totalUnits <= 1) {
          // Trash click — remove every line in this signature group.
          sigIndices.forEach((idx) => { updates[idx] = 0; });
        } else {
          // Decrement one PetTag. Prefer a line with qty > 1 to avoid pruning a line.
          let targetIdx = sigIndices.find((idx) => allItems[idx].quantity > 1);
          if (targetIdx === undefined) targetIdx = sigIndices[sigIndices.length - 1];
          updates[targetIdx] = allItems[targetIdx].quantity - 1;
        }

        const res = await this._fetchWithRetry('/cart/update.js', {
          method: 'POST',
          headers: FETCH_HEADERS,
          credentials: 'same-origin',
          body: JSON.stringify({ updates }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Bundle decrement failed: ${res.status}`);
        }
        await this.refresh();
      } catch (err) {
        console.error('[dpc-cart] bundle decrement failed:', err);
        this._showError(err.message || 'Could not update bundle');
      } finally {
        this._setLoading(false);
      }
    }

    /* ─────────── LOADING + ERROR STATES ─────────── */

    _setLoading(loading) {
      this.isLoading = loading;
      this.classList.toggle('is-loading', loading);
    }

    _showError(message) {
      // Lightweight toast: render in the drawer header area
      const existing = this.querySelector('.dpc-error');
      if (existing) existing.remove();
      const el = document.createElement('div');
      el.className = 'dpc-error';
      el.setAttribute('role', 'alert');
      el.textContent = message;
      const inner = this.querySelector('[data-content]');
      if (inner) inner.prepend(el);
      setTimeout(() => el.remove(), 4000);
    }
  }

  if (!customElements.get('dpc-cart-drawer')) {
    customElements.define('dpc-cart-drawer', DpcCartDrawer);
  }

  /* ─────────── GLOBAL HELPER (so despi-pettag.js can call it cleanly) ─────────── */

  window.dpcCart = {
    /** Add items (array of {id, quantity}) and open the drawer */
    add(items) {
      const drawer = document.querySelector('dpc-cart-drawer');
      if (!drawer) {
        console.error('[dpc-cart] No <dpc-cart-drawer> in DOM');
        return Promise.reject(new Error('No drawer'));
      }
      return drawer.addItems(items);
    },
    open() {
      const drawer = document.querySelector('dpc-cart-drawer');
      if (drawer) drawer.open();
    },
    close() {
      const drawer = document.querySelector('dpc-cart-drawer');
      if (drawer) drawer.close();
    },
  };

  /* ─────────── HEADER CART BUTTON ───────────
     Any element with `[data-cart-open]` (header icon, mobile nav, etc.) opens
     the drawer when clicked. Listener is attached at document level so buttons
     can live anywhere outside the <dpc-cart-drawer> custom element. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest && event.target.closest('[data-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    window.dpcCart.open();
  });
})();
